import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { api } from '../api';
import HOATrack from './HOATrack';

const HOATrackWidget = () => {
  const { user } = useContext(AuthContext);
  const [dashboardData, setDashboardData] = useState({
    recent_submissions: [],
    status_counts: {},
    total_active: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.hoa_id) {
      loadDashboardData();
    }
  }, [user?.hoa_id]);

  const loadDashboardData = async () => {
    try {
      const response = await api.get('/api/settings/dashboard-data', {
        params: { hoa_id: user.hoa_id }
      });
      setDashboardData(response.data.dashboard || {
        recent_submissions: [],
        status_counts: {},
        total_active: 0
      });
    } catch (error) {
      console.error('Failed to load HOA Track dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Helpers defined before usage to avoid TDZ runtime errors
  const getWidgetTitle = () => {
    if (dashboardData.team_role === 'maintenance') {
      return '🔧 Maintenance Tracker';
    } else if (dashboardData.team_role === 'security') {
      return '🛡️ Security Tracker';
    }
    return 'My HOA Requests';
  };

  function getEmptyStateMessage() {
    if (dashboardData.team_role === 'maintenance') {
      return 'No maintenance requests assigned to you';
    } else if (dashboardData.team_role === 'security') {
      return 'No security requests assigned to you';
    }
    return 'No requests submitted yet';
  }

  if (!dashboardData.recent_submissions || dashboardData.recent_submissions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">My HOA Requests</h3>
          <span className="text-sm text-gray-500">No active requests</span>
        </div>
        <div className="text-center py-4">
          <div className="text-gray-400 mb-2">
            {dashboardData.team_role === 'maintenance' ? '🔧' : 
             dashboardData.team_role === 'security' ? '🛡️' : '📋'}
          </div>
          <p className="text-sm text-gray-600">{getEmptyStateMessage()}</p>
          <p className="text-xs text-gray-500 mt-1">
            {dashboardData.is_team_member ? 
              'Assigned requests will appear here' : 
              'Your submitted forms will appear here'
            }
          </p>
        </div>
      </div>
    );
  }

  const navigateToHOATrack = (submissionId = null) => {
    // Dispatch custom event to switch to HOA Track tab and optionally select a submission
    window.dispatchEvent(new CustomEvent('switchToTab', {
      detail: { 
        tab: 'hoa_track',
        submissionId: submissionId 
      }
    }));
  };


  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{getWidgetTitle()}</h3>
        <div className="flex items-center space-x-4">
          {dashboardData.total_active > 0 && (
            <span className="text-sm text-blue-600 font-medium">
              {dashboardData.total_active} active
            </span>
          )}
          <span className="text-sm text-gray-500">
            {dashboardData.recent_submissions.length} total
          </span>
        </div>
      </div>

      {/* Status Overview */}
      {Object.keys(dashboardData.status_counts).length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(dashboardData.status_counts).map(([status, count]) => (
              <span
                key={status}
                className={`text-xs px-2 py-1 rounded-full capitalize ${
                  status === 'approved' ? 'bg-green-100 text-green-800' :
                  status === 'rejected' ? 'bg-red-100 text-red-800' :
                  status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                  status === 'need_info' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {count} {status.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent Submissions */}
      <div className="space-y-3">
        {dashboardData.recent_submissions.map(submission => (
          <div 
            key={submission.id} 
            onClick={() => navigateToHOATrack(submission.id)}
            className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h4 className="font-medium text-sm text-gray-900">{submission.template_name}</h4>
                <p className="text-xs text-gray-500">
                  Submitted {new Date(submission.created_at).toLocaleDateString()}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded capitalize whitespace-nowrap ${
                submission.status === 'approved' ? 'bg-green-100 text-green-800' :
                submission.status === 'rejected' ? 'bg-red-100 text-red-800' :
                submission.status === 'in_review' ? 'bg-blue-100 text-blue-800' :
                submission.status === 'need_info' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {submission.status.replace('_', ' ')}
              </span>
            </div>
            
            {/* Mini HOA Track Progress */}
            <div className="mt-2">
              <HOATrack status={submission.status} />
            </div>
            
            {/* Assignment info */}
            {submission.assigned_to !== 'board' && (
              <div className="mt-2 text-xs text-gray-600">
                Assigned to: <span className="capitalize font-medium">{submission.assigned_to.replace('_', ' ')}</span>
                {submission.assigned_to_details && ` (${submission.assigned_to_details})`}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* View All Link */}
      <div className="mt-4 pt-3 border-t">
        <button 
          onClick={() => navigateToHOATrack()}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          View All Requests →
        </button>
      </div>
    </div>
  );
};

export default HOATrackWidget;